import useBusinessCategories from '../useBusinessCategories';
// src/pages/BusinessProfileCreation.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFirestore, doc, setDoc, collection, serverTimestamp,
  getDoc, query, where, getDocs
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import { FiCheckCircle, FiBriefcase, FiCreditCard, FiCamera, FiX, FiAlertTriangle, FiFileText, FiUpload } from 'react-icons/fi';
import './BusinessProfileCreation.css';
import useImageCrop from '../components/useImageCrop';


const ACCOUNT_TYPES = ['Cheque/Current', 'Savings', 'Business'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_ADDITIONAL_IMAGES = 4;
const MAX_DOCUMENT_SIZE_MB = 10;

const DEFAULT_HOURS = () =>
  DAYS.reduce((acc, day) => {
    acc[day] = { open: '09:00', close: '17:00', closed: false };
    return acc;
  }, {});

const hoursToString = (hoursObj) =>
  DAYS.map(day => {
    const h = hoursObj[day];
    return h.closed ? `${day}: Closed` : `${day}: ${h.open} - ${h.close}`;
  }).join(', ');

  const MAX_COMPANY_DOCUMENTS = 4;

export default function BusinessProfileCreation() {
  const categoryOptions = useBusinessCategories();
  const { openCrop, cropDialog } = useImageCrop();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const db = getFirestore();

  const [currentStep, setCurrentStep] = useState('loading'); // loading | banking | business
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [banking, setBanking] = useState({
    accountHolder: '', bankName: '', accountNumber: '', accountType: '', branchCode: '',
  });

  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [category, setCategory] = useState('');
  const [structuredHours, setStructuredHours] = useState(DEFAULT_HOURS());
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [duplicateWarnings, setDuplicateWarnings] = useState({});

  useEffect(() => {
    checkUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUserData = async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      const hasBanking = userData?.banking?.accountNumber;
      setCurrentStep(hasBanking ? 'business' : 'banking');
    } catch (err) {
      console.error('Error checking user data:', err);
      setError(`Couldn't load your account details: ${err.code || err.message}`);
      setCurrentStep('banking');
    }
  };

  const checkForDuplicate = async (field, value) => {
    if (!value || value.trim().length < 3) return;
    try {
      const businessesRef = collection(db, 'businesses');
      const q = field === 'businessName'
        ? query(businessesRef, where('businessName', '==', value.trim()))
        : query(businessesRef, where('email', '==', value.trim().toLowerCase()));
      const snapshot = await getDocs(q);
      setDuplicateWarnings(prev => ({
        ...prev,
        [field]: snapshot.docs.length > 0
          ? `A business with this ${field === 'businessName' ? 'name' : field} already exists.`
          : null,
      }));
    } catch (err) {
      console.error('Duplicate check error:', err);
    }
  };

  const handleBankingSubmit = async () => {
    if (!banking.accountHolder || !banking.bankName || !banking.accountNumber || !banking.accountType) {
      setError('Please complete all required banking fields.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        banking: { ...banking },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCurrentStep('business');
    } catch (err) {
      console.error('Error saving banking details:', err);
      setError(`Couldn't save banking details: ${err.code || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    openCrop(file, applyProfileCrop);
  };

  const applyProfileCrop = (file, preview) => {
    setProfileImage(file);
    setProfileImagePreview(preview);
  };

  const handleAdditionalImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (additionalImages.length >= MAX_ADDITIONAL_IMAGES) {
      setError(`You can only add up to ${MAX_ADDITIONAL_IMAGES} additional images.`);
      return;
    }
    e.target.value = '';
    openCrop(file, (cropped, preview) => {
      setAdditionalImages(prev => [...prev, { file: cropped, preview }]);
    }, 1);
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCompanyDocumentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (companyDocuments.length >= MAX_COMPANY_DOCUMENTS) {
      setError(`You can only upload up to ${MAX_COMPANY_DOCUMENTS} documents.`);
      e.target.value = '';
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setError('Company documents must be PDF files.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
      setError(`Each document must be under ${MAX_DOCUMENT_SIZE_MB}MB.`);
      e.target.value = '';
      return;
    }

    setError('');
    setCompanyDocuments(prev => [...prev, file]);
    e.target.value = '';
  };

  const removeCompanyDocument = (index) => {
    setCompanyDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file, pathPrefix, filename) => {
    const storage = getStorage();
    const storageRef = ref(storage, `${pathPrefix}/${filename}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const validateBusinessDetails = () => {
    if (!businessName || !description || !address || !phone || !category) {
      setError('Please fill in all required fields.');
      return false;
    }
    if (companyDocuments.length === 0) {
      setError('Please upload at least one company document (PDF) for verification.');
      return false;
    }
    if (Object.values(duplicateWarnings).some(Boolean)) {
      setError('Please resolve duplicate warnings before submitting.');
      return false;
    }
    return true;
  };

  const handleFinalSubmit = async () => {
    if (!validateBusinessDetails()) return;
    setIsLoading(true);
    setError('');

    try {
      const finalBusinessId = doc(collection(db, 'businesses')).id;

      let profileImageUrl = null;
      if (profileImage) {
        try {
          profileImageUrl = await uploadFile(
            profileImage, `businesses/${finalBusinessId}`, `profile_${Date.now()}.jpg`
          );
        } catch (err) {
          console.error('Profile image upload failed:', err);
          setError('Profile image upload failed. Check Firebase Storage rules. Continuing without it — you can retry.');
        }
      }

      const additionalImageUrls = [];
      for (let i = 0; i < additionalImages.length; i++) {
        try {
          const url = await uploadFile(
            additionalImages[i].file, `businesses/${finalBusinessId}`, `gallery_${Date.now()}_${i}.jpg`
          );
          additionalImageUrls.push(url);
        } catch (err) {
          console.error(`Additional image ${i + 1} upload failed:`, err);
        }
      }

      const companyDocumentUrls = [];
      for (let i = 0; i < companyDocuments.length; i++) {
        try {
          const url = await uploadFile(
            companyDocuments[i], `businesses/${finalBusinessId}/documents`, `company_doc_${Date.now()}_${i}.pdf`
          );
          companyDocumentUrls.push(url);
        } catch (err) {
          console.error(`Company document ${i + 1} upload failed:`, err);
          setError(`Couldn't upload document ${i + 1} (${companyDocuments[i].name}): ${err.code || err.message}. Please try again.`);
          setIsLoading(false);
          return; // documents are required — don't create the business without them
        }
      }

      const businessData = {
        businessId: finalBusinessId,
        businessName,
        description,
        address,
        phone,
        email,
        website,
        businessHours: hoursToString(structuredHours),
        category,
        profileImageUrl,
        additionalImageUrls,
        companyDocumentUrls,
        ownerId: currentUser.uid,
        status: 'active',
        isVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'businesses', finalBusinessId), businessData);
      navigate('/dashboard');
    } catch (err) {
      console.error('Error creating business profile:', err);
      setError(`Couldn't save business profile: ${err.code || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (currentStep === 'loading') {
    return <div className="business-creation-page">Loading...</div>;
  }

  return (
    <div className="business-creation-page">
      <div className="business-creation-container">
        <h1 className="business-creation-title">
          {currentStep === 'banking' ? 'Set Up Payouts' : 'Create Your Business Profile'}
        </h1>

        {error && (
          <div className="form-error">
            <FiAlertTriangle /> {error}
          </div>
        )}

        {currentStep === 'banking' && (
          <div className="step-panel">
            <h2><FiCreditCard /> Banking Details</h2>
            <p className="step-subtext">Used for payouts on completed bookings. You only need to do this once.</p>

            <label>Account Holder Name *</label>
            <input value={banking.accountHolder} onChange={e => setBanking(p => ({ ...p, accountHolder: e.target.value }))} />

            <label>Bank Name *</label>
            <input value={banking.bankName} onChange={e => setBanking(p => ({ ...p, bankName: e.target.value }))} />

            <label>Account Number *</label>
            <input value={banking.accountNumber} onChange={e => setBanking(p => ({ ...p, accountNumber: e.target.value }))} />

            <label>Branch Code</label>
            <input value={banking.branchCode} onChange={e => setBanking(p => ({ ...p, branchCode: e.target.value }))} />

            <label>Account Type *</label>
            <select value={banking.accountType} onChange={e => setBanking(p => ({ ...p, accountType: e.target.value }))}>
              <option value="">Select account type</option>
              {ACCOUNT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
            </select>

            <div className="step-actions">
              <button className="btn-primary" onClick={handleBankingSubmit} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 'business' && (
          <div className="step-panel">
            <h2><FiBriefcase /> Business Information</h2>

            <label>Main Profile Image</label>
            <div className="image-upload-row">
              {profileImagePreview ? (
                <div className="image-preview-wrapper">
                  <img src={profileImagePreview} alt="Profile preview" className="logo-preview" />
                  <button type="button" className="business-image-crop-button" onClick={() => openCrop(profileImage, applyProfileCrop)}>Crop</button>
                </div>
              ) : (
                <label className="image-picker-placeholder">
                  <FiCamera />
                  <span>Add Main Photo</span>
                  <input type="file" accept="image/*" onChange={handleProfileImageChange} hidden />
                </label>
              )}
            </div>

            <label>Additional Images ({additionalImages.length}/{MAX_ADDITIONAL_IMAGES})</label>
            <div className="additional-images-row">
              {additionalImages.map((img, index) => (
                <div key={index} className="image-preview-wrapper small">
                  <button type="button" className="business-image-crop-button" aria-label={`Crop additional image ${index + 1}`}
                    onClick={() => openCrop(img.file, (file, preview) => setAdditionalImages(prev => prev.map((item, i) => i === index ? { file, preview } : item)), 1)}>Crop</button>
                  <img src={img.preview} alt={`Additional ${index + 1}`} className="logo-preview small" />
                  <button className="remove-image-btn" onClick={() => removeAdditionalImage(index)}>
                    <FiX />
                  </button>
                </div>
              ))}
              {additionalImages.length < MAX_ADDITIONAL_IMAGES && (
                <label className="image-picker-placeholder small">
                  <FiCamera />
                  <input type="file" accept="image/*" onChange={handleAdditionalImageChange} hidden />
                </label>
              )}
            </div>

            <label>Business Name *</label>
            <input
              value={businessName}
              onChange={e => { setBusinessName(e.target.value); setDuplicateWarnings(p => ({ ...p, businessName: null })); }}
              onBlur={() => checkForDuplicate('businessName', businessName)}
            />
            {duplicateWarnings.businessName && <p className="field-warning">{duplicateWarnings.businessName}</p>}

            <label>Description *</label>
            <textarea rows={4} value={description} onChange={e => setDescription(e.target.value)} />

            <label>Address *</label>
            <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, suburb, city" />

            <label>Phone *</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} />

            <label>Business Email</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setDuplicateWarnings(p => ({ ...p, email: null })); }}
              onBlur={() => checkForDuplicate('email', email)}
            />
            {duplicateWarnings.email && <p className="field-warning">{duplicateWarnings.email}</p>}

            <label>Website</label>
            <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />

            <label>Category *</label>
            {categoryOptions.error && <p role="alert">{categoryOptions.error} <button type="button" onClick={categoryOptions.refresh}>Retry categories</button></p>}
          <select value={category} onChange={e => setCategory(e.target.value)}>
              <option value="">Select business category</option>
              {categoryOptions.categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>

            <label>Business Hours</label>
            <div className="hours-grid">
              {DAYS.map(day => {
                const h = structuredHours[day];
                return (
                  <div key={day} className="hours-row">
                    <span className="hours-day">{day}</span>
                    <label className="hours-closed-toggle">
                      <input
                        type="checkbox"
                        checked={!h.closed}
                        onChange={() => setStructuredHours(p => ({ ...p, [day]: { ...p[day], closed: !p[day].closed } }))}
                      />
                      Open
                    </label>
                    {!h.closed && (
                      <>
                        <input
                          type="time"
                          value={h.open}
                          onChange={e => setStructuredHours(p => ({ ...p, [day]: { ...p[day], open: e.target.value } }))}
                        />
                        <span>–</span>
                        <input
                          type="time"
                          value={h.close}
                          onChange={e => setStructuredHours(p => ({ ...p, [day]: { ...p[day], close: e.target.value } }))}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <label>Company Documents (PDF) *</label>
            <p className="step-subtext">
              Used to verify your business — e.g. registration certificate, CIPC document, or ID. Up to {MAX_COMPANY_DOCUMENTS} files.
            </p>
            <div className="document-upload-row">
              {companyDocuments.map((file, index) => (
                <div key={index} className="document-preview">
                  <FiFileText />
                  <span className="document-name">{file.name}</span>
                  <button className="remove-image-btn" onClick={() => removeCompanyDocument(index)}>
                    <FiX />
                  </button>
                </div>
              ))}
              {companyDocuments.length < MAX_COMPANY_DOCUMENTS && (
                <label className="document-picker-placeholder">
                  <FiUpload />
                  <span>Upload PDF ({companyDocuments.length}/{MAX_COMPANY_DOCUMENTS})</span>
                  <input type="file" accept="application/pdf,.pdf" onChange={handleCompanyDocumentChange} hidden />
                </label>
              )}
            </div>

            <div className="step-actions">
              <button className="btn-primary" onClick={handleFinalSubmit} disabled={isLoading || Boolean(cropDialog)}>
                {isLoading ? 'Creating...' : (<><FiCheckCircle /> Create Profile</>)}
              </button>
            </div>
          </div>
        )}
      </div>
      {cropDialog}
    </div>
  );
}
